import { AwsRdsAccesses } from "@p0security/integrations-aws-rds-shared/accesses";
import { GcpCloudSqlAccesses } from "@p0security/integrations-gcp-cloudsql-shared/accesses";
import { findDelegate } from "@p0security/shared/access/util";
import type { Maturity } from "@p0security/shared/integrations/shared";
import { Access, Delegate } from "@p0security/shared/types/access/index";
import type {
  RequestOf,
  StagedRequestOf,
} from "@p0security/shared/types/access/index";
import { addOptionalQuotes } from "@p0security/util/quote";
import type { DistributedOmit } from "@p0security/util/types/util";

const generated = {
  // Name of the role used for access.
  name: { type: "string" },
  // Connection identity and target for the granted database access.
  dbUser: { type: "string" },
  hostname: { type: "string" },
  port: { type: "string" },
} as const;

/** Defines the role and SQL accesses at one maturity
 *
 * PostgreSQL and MySQL share these accesses but not their maturity, so each
 * integration registers its own copy.
 */
export const newDbAccessDefinitions = (maturity: Maturity) => ({
  role: Access({
    label: "Role",
    description: "A database role",
    maturity,
    delegation: {
      "aws-rds": Delegate({
        resource: "aws-rds",
        key: "resource" as const,
        accesses: AwsRdsAccesses,
      }),
      "gcp-cloudsql": Delegate({
        resource: "gcp-cloudsql",
        key: "resource" as const,
        accesses: GcpCloudSqlAccesses,
      }),
    },
    generated,
    parameters: {
      instance: {
        type: "static",
        label: "The instance (or cluster) to access",
        optional: true,
      },
      instanceId: {
        type: "generated",
        label: "The instance (or cluster) to access",
      },
      database: {
        type: "static",
        label: "The database to use",
        description:
          "May be omitted if your organization has a default database",
        optional: true,
      },
      name: {
        type: "dynamic",
        label: "The requested role",
      },
    },
    render: { locator: "instanceId", policy: "name" } as const,
  }),
  sql: Access({
    label: "Sql",
    description: "Infer required permissions by parsing a SQL script",
    maturity,
    delegation: {
      "aws-rds": Delegate({
        resource: "aws-rds",
        key: "resource" as const,
        accesses: AwsRdsAccesses,
      }),
      "gcp-cloudsql": Delegate({
        resource: "gcp-cloudsql",
        key: "resource" as const,
        accesses: GcpCloudSqlAccesses,
      }),
    },
    generated,
    parameters: {
      instance: {
        type: "static",
        label: "The instance (or cluster) to access",
        optional: true,
      },
      instanceId: {
        type: "generated",
        label: "The instance (or cluster) to access",
      },
      database: {
        type: "static",
        label: "The database to use",
        description:
          "May be omitted if your organization has a default database",
        optional: true,
      },
      query: {
        type: "input",
        label: "Query",
        multiline: true,
        description:
          "The body of the SQL script - may contain multiple SQL statements",
      },
      policies: {
        label: "Policies",
        description: "The entitlements in the Postgres instance to be granted",
        multivalued: true,
        type: "object",
      },
    },
    render: { locator: "instanceId", policy: "query" } as const,
  }),
});

/** The database accesses in preview, as MySQL registers them */
export const DbAccesses = newDbAccessDefinitions("preview");

const CONNECT_PREFIX =
  "👉 To connect to your instance, please run the following command in the P0 CLI, then follow the printed instructions.";

/** Attaches arch-specific CLI-command instructions to `accesses` (by default {@link DbAccesses}) */
export const newDbAccesses = (
  arch: string,
  accesses: DbAccesses = DbAccesses
) => ({
  role: {
    ...accesses.role,
    render: {
      ...accesses.role.render,
      instructions: (
        request: DistributedOmit<StagedRequestOf<DbRoleAccess>, "requestId">
      ) => {
        const { permission, delegation } = request;
        if (!findDelegate(delegation, "aws-rds")) return undefined;
        const command = `\`p0 aws rds generate-db-auth-token --role ${permission.name} --arch ${arch} --instance ${permission.instanceId}\``;
        return CONNECT_PREFIX + "\n\n" + command;
      },
    },
  },
  sql: {
    ...accesses.sql,
    render: {
      ...accesses.sql.render,
      instructions: (
        request: DistributedOmit<StagedRequestOf<DbSqlAccess>, "requestId">
      ) => {
        const { permission, delegation } = request;
        if (!findDelegate(delegation, "aws-rds")) return undefined;
        const databaseArgument = permission.database
          ? ` --database ${addOptionalQuotes(permission.database)}`
          : "";
        const command = `p0 aws rds generate-db-auth-token --sql ${addOptionalQuotes(permission.query)} --arch ${arch} --instance ${permission.instanceId}${databaseArgument}`;
        return CONNECT_PREFIX + "\n\n```\n" + command + "\n```";
      },
    },
  },
});

export type DbAccesses = typeof DbAccesses;
export type DbAccess = DbAccesses[keyof DbAccesses];

export type DbRoleAccess = DbAccesses["role"];
export type DbRoleRequest = RequestOf<DbRoleAccess>;

export type DbSqlAccess = DbAccesses["sql"];
export type DbSqlRequest = RequestOf<DbSqlAccess>;
